import { useState, useRef, useEffect } from "react";
import { ArrowRight, Check, Truck, RefreshCw, Lock, Banknote, MapPin, Package, Plus, Minus } from "lucide-react";
import { C, SERIF, SANS, REVIEWS } from "../lib/constants";
import { Btn, Stars, Eyebrow, Accordion, ImgPh } from "../components/Primitives";
import { useSEO } from "../lib/useSEO";
import { SEO } from "../lib/seoRoutes";

// Mobile swipe gallery — hidden on desktop via CSS.
// Renders all images side-by-side inside a scroll-snap container so users
// can flick between them with a native touch gesture. Dots reflect the
// currently visible image.
function MobileSwipeGallery({ gallery, activeIndex, onChange, coral, cream, muted, border }) {
  const trackRef = useRef(null);
  const [internal, setInternal] = useState(activeIndex || 0);

  // Sync scroll position when parent changes active image (e.g. dot tap).
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.children[activeIndex];
    if (slide && Math.abs(el.scrollLeft - slide.offsetLeft) > 4) {
      el.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    }
    setInternal(activeIndex);
  }, [activeIndex]);

  // Update active index as the user swipes.
  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== internal) {
      setInternal(i);
      onChange && onChange(i);
    }
  };

  return (
    <div className="pp-gallery-mobile" data-testid="mobile-product-gallery">
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="pp-swipe-track"
        data-testid="mobile-gallery-track"
      >
        {gallery.map((g, i) => (
          <div key={g.label} className="pp-swipe-slide" style={{ background: cream }}>
            <img
              src={g.src}
              alt={g.label}
              loading={i === 0 ? "eager" : "lazy"}
              decoding={i === 0 ? "sync" : "async"}
              fetchpriority={i === 0 ? "high" : "low"}
              style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", borderRadius: 16 }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
            {i === 0 && (
              <span style={{
                position: "absolute", top: 12, left: 12, background: coral,
                color: "#fff", fontFamily: SANS, fontSize: 10, fontWeight: 700,
                padding: "4px 12px", borderRadius: 14
              }}>NEW & IMPROVED</span>
            )}
          </div>
        ))}
      </div>
      <div className="pp-swipe-dots" role="tablist" aria-label="Product images">
        {gallery.map((g, i) => (
          <button
            key={g.label}
            role="tab"
            aria-selected={internal === i}
            aria-label={`Show image ${i + 1}: ${g.label}`}
            data-testid={`mobile-gallery-dot-${i}`}
            onClick={() => onChange && onChange(i)}
            className="pp-swipe-dot"
            style={{
              background: internal === i ? coral : border,
              width: internal === i ? 22 : 8,
            }}
          />
        ))}
      </div>
      <div style={{ marginTop: 6, fontFamily: SANS, fontSize: 12, color: muted, textAlign: "center" }}>
        {gallery[internal]?.label}
      </div>
    </div>
  );
}

export default function ProductPage({ addToCart, go }) {
  useSEO(SEO.product);
  const [size, setSize] = useState("M-L");
  const [qty, setQty] = useState(1);
  const [sub, setSub] = useState(false);
  const [img, setImg] = useState(0);
  const [added, setAdded] = useState(false);
  const base = 299, price = sub ? 254 : base;
  const gallery = [
    { label: "Comfort that understands her", src: "/img/g1_hero.webp" },
    { label: "Find your perfect size", src: "/img/g2_size.webp" },
    { label: "Why we're better for you", src: "/img/g3_compare.webp" },
    { label: "Comfort through long hours", src: "/img/g4_comfort.webp" },
  ];

  const handleAdd = () => {
    addToCart({ size, qty, sub, price });
    setAdded(true); setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "44px 28px 72px" }}>
      <div style={{ fontFamily: SANS, fontSize: 12, color: C.muted, marginBottom: 32 }}>
        <span onClick={() => go("home")} style={{ cursor: "pointer" }}>Home</span>
        {" → Shop → "}
        <span style={{ color: C.dark }}>Disposable Period Panties</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 56 }}>
        {/* Gallery */}
        <div>
          {/* Desktop gallery (unchanged) */}
          <div className="pp-gallery-desktop">
            <div style={{ position: "relative", background: C.cream, borderRadius: 16, height: 500 }}>
              <img src={gallery[img].src} alt={gallery[img].label} style={{
                width: "100%", height: "100%", objectFit: "contain", borderRadius: 16, display: "block",
              }} onError={(e) => { e.target.style.display = "none"; }} />
              <span style={{ position: "absolute", top: 16, left: 16, background: C.coral,
                color: "#fff", fontFamily: SANS, fontSize: 10, fontWeight: 700,
                padding: "4px 12px", borderRadius: 14 }}>NEW & IMPROVED</span>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {gallery.map((g, i) => (
                <button key={g.label} onClick={() => setImg(i)} data-testid={`gallery-thumb-${i}`} style={{
                  flex: 1, height: 72, borderRadius: 8, cursor: "pointer",
                  border: `2px solid ${img === i ? C.coral : C.border}`,
                  overflow: "hidden", padding: 0, background: C.cream,
                }}>
                  <img src={g.src} alt={g.label} style={{
                    width: "100%", height: "100%", objectFit: "contain", display: "block",
                  }} onError={(e) => { e.target.style.display = "none"; }} />
                </button>
              ))}
            </div>
            <div style={{ marginTop: 8, fontFamily: SANS, fontSize: 12, color: C.muted, textAlign: "center" }}>
              {gallery[img].label}
            </div>
          </div>

          {/* Mobile swipe gallery */}
          <MobileSwipeGallery
            gallery={gallery}
            activeIndex={img}
            onChange={setImg}
            coral={C.coral}
            cream={C.cream}
            muted={C.muted}
            border={C.border}
          />
        </div>
        {/* Info */}
        <div>
          <Eyebrow>4Ever</Eyebrow>
          <h1 style={{ fontFamily: SERIF, fontSize: 34, color: C.dark, fontWeight: 400,
            margin: "0 0 8px" }}>Disposable Period Panties</h1>
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
            <span style={{ background: C.greenBg, color: C.green, fontSize: 11,
              padding: "4px 10px", borderRadius: 12, fontWeight: 600, fontFamily: SANS }}>
              Clinical study — zero irritation*
            </span>
            <span style={{ fontFamily: SANS, fontSize: 11, color: C.mutedLt }}>
              *IS 4011 (2018) PCD 19, Cliantha Research
            </span>
          </div>
          <div style={{ fontFamily: SANS, marginBottom: 20 }}>
            <span style={{ fontSize: 30, fontWeight: 800, color: C.dark }}>₹{price}</span>
            {sub && <span style={{ fontSize: 15, color: C.mutedLt,
              textDecoration: "line-through", marginLeft: 8 }}>₹{base}</span>}
            <span style={{ fontSize: 13, color: C.muted, marginLeft: 6 }}>/ pack of 5</span>
          </div>
          {/* Purchase type */}
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {[["One-time purchase", `₹${base}/pack`, false],
              ["Subscribe & Save 15%", `₹254/pack · Cancel anytime`, true]].map(([l, p, s]) => (
              <button key={l} onClick={() => setSub(s)} style={{ flex: 1,
                padding: "12px 14px", borderRadius: 8, cursor: "pointer",
                textAlign: "left", border: `2px solid ${sub === s ? C.coral : C.border}`,
                background: sub === s ? C.coralBg : C.white }}>
                <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 700,
                  color: sub === s ? C.coral : C.dark, marginBottom: 2 }}>{l}</div>
                <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted }}>{p}</div>
              </button>
            ))}
          </div>
          {sub && (
            <div style={{ background: C.greenBg, borderRadius: 8, padding: "10px 14px",
              fontFamily: SANS, fontSize: 12, color: C.green, marginBottom: 16,
              lineHeight: 1.6 }}>
              🌿 Delivery auto-synced to your period tracker — arrives 2 days before
              your predicted period. Pause or cancel anytime from your account.
            </div>
          )}
          {/* Size */}
          <div style={{ marginBottom: 18, marginTop: sub ? 0 : 8 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600,
                color: C.dark }}>Size: {size}</span>
              <span onClick={() => go("sizeguide")} style={{ fontFamily: SANS,
                fontSize: 11, color: C.coral, textDecoration: "underline",
                cursor: "pointer" }}>Size Guide</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["S-M", "M-L", "L-XL", "XL-XXL", "XXL-XXXL"].map(s => (
                <button key={s} onClick={() => setSize(s)} style={{
                  minWidth: 68, height: 50, padding: "0 10px",
                  borderRadius: 6, cursor: "pointer",
                  border: `2px solid ${size === s ? C.coral : C.border}`,
                  background: size === s ? C.coralBg : C.white, fontFamily: SANS,
                  fontSize: 13, fontWeight: 700,
                  color: size === s ? C.coral : C.dark }}>{s}</button>
              ))}
            </div>
            <div style={{ fontFamily: SANS, fontSize: 11, color: C.muted, marginTop: 6 }}>
              Waist 22–52 in · Hip 32–54 in · Size up for a comfortable fit
            </div>
          </div>
          {/* Qty */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: C.dark,
              marginBottom: 8 }}>Quantity (packs)</div>
            <div style={{ display: "flex", alignItems: "center",
              border: `1px solid ${C.border}`, borderRadius: 6, width: "fit-content",
              background: C.white }}>
              <button onClick={() => setQty(Math.max(1, qty - 1))} style={{ width: 38,
                height: 38, background: "none", border: "none", cursor: "pointer",
                color: C.dark, display: "flex", alignItems: "center",
                justifyContent: "center" }}><Minus size={13} /></button>
              <span style={{ width: 38, textAlign: "center", fontFamily: SANS,
                fontSize: 15, fontWeight: 700, color: C.dark }}>{qty}</span>
              <button onClick={() => setQty(qty + 1)} style={{ width: 38, height: 38,
                background: "none", border: "none", cursor: "pointer", color: C.dark,
                display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={13} /></button>
            </div>
          </div>
          <div style={{ fontFamily: SANS, fontSize: 14, color: C.muted, marginBottom: 12 }}>
            Total: <span style={{ fontWeight: 800, color: C.dark,
              fontSize: 20 }}>₹{price * qty}</span>
            <span style={{ fontSize: 12, marginLeft: 6 }}>({qty * 5} panties)</span>
          </div>
          <Btn full size="lg" onClick={handleAdd} data-testid="add-to-bag-btn"
            style={added ? { background: C.green } : {}}>
            {added ? <><Check size={16} /> Added to Bag!</> :
              <>Add to Bag · ₹{price * qty}</>}
          </Btn>
          <div data-testid="product-trust-badges" className="pp-trust-badges" style={{ display: "flex", gap: 14, justifyContent: "center",
            padding: "12px 0", borderTop: `1px solid ${C.border}`, marginTop: 12,
            flexWrap: "wrap" }}>
            {[["free-shipping", <Truck size={12} />, "Free shipping over ₹999"],
              ["returns", <RefreshCw size={12} />, "Easy 7-day returns"],
              ["cod", <Banknote size={12} />, "COD available"],
              ["secure", <Lock size={12} />, "Secure payments"],
              ["made-in-india", <MapPin size={12} />, "Made in India"]].map(([k, i, t]) => (
              <span key={k} data-testid={`trust-badge-${k}`} style={{ display: "flex", alignItems: "center", gap: 5,
                fontFamily: SANS, fontSize: 11, color: C.muted }}>
                <span style={{ color: C.coral }}>{i}</span>{t}</span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", fontFamily: SANS,
            fontSize: 12, color: C.green, marginBottom: 16 }}>
            <Package size={13} /> In stock — ships today from Mumbai
          </div>
          {/* Tabs as accordions */}
          {[
            ["What's inside", "5 individually wrapped disposable period panties. Full brief cut, soft elasticated waist, leak-proof core (holds up to 40ml), breathable outer layer. Dermatologically tested."],
            ["Materials & certifications", "Top layer: 100% soft cotton. Core: super-absorbent polymer. Outer: breathable PE film. OEKO-TEX Standard 100 certified. No fragrance, no chlorine bleach, no phthalates, no parabens."],
            ["How to use", "Wear like regular underwear. Lasts up to 12 hours* depending on usage — change sooner on heavy-flow days. Roll and wrap in the individual packet to dispose hygienically. Do not flush."],
            ["Shipping & returns", "Free shipping on orders ₹999+. Standard delivery 3–5 business days. Express 1–2 days (₹99, most metros). 7-day returns on unopened packs. Cash on Delivery available."],
          ].map(([t, c]) => <Accordion key={t} title={t} content={c} />)}
        </div>
      </div>
      {/* Reviews section — hidden until real customer reviews arrive */}
      {/* Subscription upsell */}
      {!sub && (
        <div style={{ marginTop: 44, background: C.coralBg, borderRadius: 16,
          padding: "28px 32px", display: "flex", justifyContent: "space-between",
          alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: SERIF, fontSize: 22, color: C.dark,
              marginBottom: 4 }}>Complete your ritual</div>
            <div style={{ fontFamily: SANS, fontSize: 13, color: C.muted }}>
              Subscribe and your delivery arrives before your period — automatically.
              Save ₹45 every order.</div>
          </div>
          <Btn onClick={() => setSub(true)}>Switch to subscription</Btn>
        </div>
      )}
    </div>
  );
}
